import { ChatStoreContext } from '@/components/chat/ChatStore/ChatStoreContext'
import { CreatorlandLogo } from '@/components/commons'
import { SocialIconLink } from '@/components/commons/links/SocialIconLink.component'
import { ISocialPlatform } from '@/interfaces/socials'
import { environment } from '@/shared/consts/environment'
import { CREATORLAND_SOCIALS } from '@/shared/consts/pages'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import clsx from 'clsx'
import { useChatConnectionsRequest } from 'containers/chat/hooks/useChatConnectionsRequest'
import { HightlightFormModal } from 'containers/highlights/components/HighlightFormModal'
import {
  useInvitationsReceived,
  useNewConnections,
} from 'containers/profile-network/hooks/useConnections'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Icon } from 'public/assets/v2/images/icons'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { BrowserContext, UserContext } from '../../../context'
import { ProfilePageContext } from '../../../context/profilePage'
import {
  fontNavigationListItem,
  footerContainer,
  footerLogo,
  footerLogoContainer,
  footerLogoCopyright,
  footerNavigationContainer,
  footerNavigationInternalLinksContainer,
  footerNavigationList,
  footerNavigationSectionTitle,
  footerNavigationSocialContainer,
  footerNavigationSocialLinks,
  slideAnimationStartPositionVariable,
  slidingBackgroundContainer,
  slidingBackgroundContainerHighlight,
  userFooterContainer,
  userFooterMenuContainer,
  userFooterMenuItem,
  userFooterMenuItemIcon,
  userFooterMenuItemLabel,
  userFooterMenuItemNotificationDot,
} from './Footer.css'

export const Footer = ({ className = '' }) => {
  const {
    isAuthenticated,
    user,
    logout: logoutContext,
  } = React.useContext(UserContext)
  const userName = user?.username?.toLocaleLowerCase()
  const { isDesktop } = React.useContext(BrowserContext)
  const { hasUnreadMessages, connectionChannelsUnreadMessagesCount } =
    useContext(ChatStoreContext)

  const requestConnections = useChatConnectionsRequest()

  const { invitationsCount } = useInvitationsReceived()
  const { newConnectionsCount } = useNewConnections()
  const menuItemsRef = useRef<HTMLElement[]>([])
  const backgroundElementRef = useRef<HTMLDivElement>(null)
  const activeMenuItem = useActiveMenuItem()
  const previousActiveMenuItem = usePrevious(activeMenuItem)

  const totalUnreadMessages = useMemo(
    () => connectionChannelsUnreadMessagesCount + requestConnections?.length,
    [connectionChannelsUnreadMessagesCount, requestConnections?.length]
  )

  const unreadMessageCountLabel = useMemo(() => {
    if (!totalUnreadMessages) return ''

    if (totalUnreadMessages > 99) return '99+'

    return `${totalUnreadMessages}`
  }, [totalUnreadMessages])

  const activeElement = menuItemsRef.current.find((el) => {
    if (!el) return null
    const itemName = el.dataset.pathname
    return itemName === activeMenuItem
  })

  const slideAnimationStartPosition = useSlideAnimationStartPosition(
    activeMenuItem,
    previousActiveMenuItem,
    menuItemsRef
  )

  const logout = () => logoutContext()
  const currentYear = new Date().getFullYear()
  const [showModal, setShowModal] = useState(false)
  const networkNotificationsCount = newConnectionsCount + invitationsCount

  /*
   * Logged-in mobile footer
   */
  if (isAuthenticated && !isDesktop) {
    return (
      <>
        <HightlightFormModal
          show={showModal}
          onClose={() => setShowModal(false)}
        />
        <div className={userFooterContainer}>
          <ul className={userFooterMenuContainer}>
            <Link href={`/inbox`}>
              <li
                ref={(el) => menuItemsRef.current.push(el)}
                data-pathname={'inbox'}
                className={userFooterMenuItem}
                data-is-active={activeMenuItem === 'inbox'}
                data-is-unread-inbox={
                  hasUnreadMessages || !!totalUnreadMessages
                }
              >
                <span className={userFooterMenuItemIcon}>
                  {activeMenuItem !== 'inbox' &&
                  (hasUnreadMessages || !!totalUnreadMessages) ? (
                    <Icon.InboxUnreadIcon />
                  ) : (
                    <Icon.InboxIcon />
                  )}
                </span>

                {(hasUnreadMessages || !!totalUnreadMessages) && (
                  <span
                    data-is-unread-message={true}
                    className={userFooterMenuItemLabel}
                  >
                    {unreadMessageCountLabel}
                  </span>
                )}

                {!activeElement && activeMenuItem === 'inbox' ? (
                  <div
                    ref={backgroundElementRef}
                    className={slidingBackgroundContainer}
                    style={assignInlineVars({
                      [slideAnimationStartPositionVariable]:
                        slideAnimationStartPosition,
                    })}
                  />
                ) : null}
              </li>
            </Link>

            <Link href={`/${userName}/network`}>
              <li
                ref={(el) => menuItemsRef.current.push(el)}
                data-pathname={'network'}
                className={userFooterMenuItem}
                data-is-active={activeMenuItem === 'network'}
              >
                <span className={userFooterMenuItemIcon}>
                  <Icon.UsersIcon />
                  {networkNotificationsCount > 0 ? (
                    <div className={userFooterMenuItemNotificationDot} />
                  ) : null}
                </span>

                {!activeElement && activeMenuItem === 'network' ? (
                  <div
                    ref={backgroundElementRef}
                    className={slidingBackgroundContainer}
                    style={assignInlineVars({
                      [slideAnimationStartPositionVariable]:
                        slideAnimationStartPosition,
                    })}
                  />
                ) : null}
              </li>
            </Link>

            <Link href="#" scroll={false} onClick={() => setShowModal(true)}>
              <li className={userFooterMenuItem} data-is-active={true}>
                <Icon.PlusIcon className={userFooterMenuItemIcon} />
                <div
                  ref={backgroundElementRef}
                  className={slidingBackgroundContainerHighlight}
                />
              </li>
            </Link>

            {/* <Link href={'/discover'}>
              <li
                ref={(el) => menuItemsRef.current.push(el)}
                data-pathname={'discover'}
                className={userFooterMenuItem}
                data-is-active={activeMenuItem === 'discover'}
              >
                <span className={userFooterMenuItemIcon}>
                  <Icon.DiscoverIcon />
                </span>

                {!activeElement && activeMenuItem === 'discover' ? (
                  <div
                    ref={backgroundElementRef}
                    className={slidingBackgroundContainer}
                    style={assignInlineVars({
                      [slideAnimationStartPositionVariable]:
                        slideAnimationStartPosition,
                    })}
                  />
                ) : null}
              </li>
            </Link> */}

            <Link href={`/${userName}`}>
              <li
                ref={(el) => menuItemsRef.current.push(el)}
                data-pathname={'profile'}
                className={userFooterMenuItem}
                data-is-active={activeMenuItem === 'profile'}
              >
                <Icon.UserIcon className={userFooterMenuItemIcon} />
                {!activeElement && activeMenuItem === 'profile' ? (
                  <div
                    ref={backgroundElementRef}
                    className={slidingBackgroundContainer}
                    style={assignInlineVars({
                      [slideAnimationStartPositionVariable]:
                        slideAnimationStartPosition,
                    })}
                  />
                ) : null}
              </li>
            </Link>

            {activeElement
              ? createPortal(
                  <div
                    ref={backgroundElementRef}
                    className={slidingBackgroundContainer}
                    style={assignInlineVars({
                      [slideAnimationStartPositionVariable]:
                        slideAnimationStartPosition,
                    })}
                  />,
                  activeElement
                )
              : null}
          </ul>
        </div>
      </>
    )
  }

  /*
   * Standard footer
   */
  return (
    <div className={clsx(footerContainer, className)}>
      <div className={footerLogoContainer}>
        <div className={footerLogo}>
          <Link href="/">
            <CreatorlandLogo />
          </Link>
        </div>

        <div className={footerLogoCopyright}>
          Copyright ©{currentYear}. Yeet, Inc. All Rights Reserved.
        </div>
      </div>

      <div className={footerNavigationContainer}>
        <div className={footerNavigationInternalLinksContainer}>
          <div>
            <h5 className={footerNavigationSectionTitle}>PLATFORM</h5>
            <ul className={footerNavigationList}>
              {isAuthenticated ? (
                <li className={`${fontNavigationListItem} cursor-pointer`}>
                  <div onClick={logout}>Logout</div>
                </li>
              ) : (
                <li className={fontNavigationListItem}>
                  <Link href="/login">Enter Creatorland</Link>
                </li>
              )}

              <li className={fontNavigationListItem}>
                <Link href="/checkout">Get Insider</Link>
              </li>

              <li className={fontNavigationListItem}>
                <Link href="/discord" target="_blank">
                  Join the Discord
                </Link>
              </li>

              {/* <li className={fontNavigationListItem}>
                <Link target="_blank" href="https://business.creatorland.com">
                  Creatorland for Business
                </Link>
              </li> */}

              <li className={fontNavigationListItem}>
                <Link
                  target="_blank"
                  href="https://newsletter.creatorland.com/c/university"
                >
                  University
                </Link>
              </li>
              <li className={fontNavigationListItem}>
                <Link
                  target="_blank"
                  href="https://chatgpt.com/g/g-67361d0e1a888190911b1688ec990a2d-creatorland-co-creator"
                >
                  🤖 Try Co-Creator (free)
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h5 className={footerNavigationSectionTitle}>Company</h5>
            <ul className={footerNavigationList}>
              <li className={fontNavigationListItem}>
                <Link href={`mailto:${environment.contactEmail}`}>Contact</Link>
              </li>

              <li className={fontNavigationListItem}>
                <Link target="_blank" href="https://newsletter.creatorland.com">
                  Newsletter
                </Link>
              </li>

              <li className={fontNavigationListItem}>
                <Link
                  target="_blank"
                  href="https://creatorland.notion.site/Job-Board-1322633cce1f4ef0bd13df8ba15e0305?pvs=4"
                >
                  Work at Creatorland!
                </Link>
              </li>

              <li className={fontNavigationListItem}>
                <Link
                  target="_blank"
                  href="https://help.creatorland.com/hc/en-us"
                >
                  Help
                </Link>
              </li>
              <li className={fontNavigationListItem}>
                <Link href="/privacy">Privacy Policy</Link>
              </li>
              <li className={fontNavigationListItem}>
                <Link href="/terms">Terms</Link>
              </li>

              <li className={fontNavigationListItem}>
                <Link href="/legal">Legal</Link>
              </li>
            </ul>
          </div>
        </div>

        <div className={footerNavigationSocialContainer}>
          <h5 className={footerNavigationSectionTitle}>Follow us</h5>
          <ul className={footerNavigationSocialLinks}>
            <SocialIconLink
              social={ISocialPlatform.TIKTOK}
              customUrl={CREATORLAND_SOCIALS[ISocialPlatform.TIKTOK]}
            />
            <SocialIconLink
              social={ISocialPlatform.INSTAGRAM}
              customUrl={CREATORLAND_SOCIALS[ISocialPlatform.INSTAGRAM]}
            />
            <SocialIconLink
              social={ISocialPlatform.YOUTUBE}
              customUrl={CREATORLAND_SOCIALS[ISocialPlatform.YOUTUBE]}
            />
            <SocialIconLink
              social={ISocialPlatform.TWITTER}
              customUrl={CREATORLAND_SOCIALS[ISocialPlatform.TWITTER]}
            />
            <SocialIconLink
              social={ISocialPlatform.DISCORD}
              customUrl={CREATORLAND_SOCIALS[ISocialPlatform.DISCORD]}
            />
            <SocialIconLink
              social={ISocialPlatform.LINKEDIN}
              customUrl={CREATORLAND_SOCIALS[ISocialPlatform.LINKEDIN]}
            />
          </ul>
        </div>
      </div>
    </div>
  )
}

function usePrevious<T>(value: T) {
  const ref = useRef<T>(null)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

function useSlideAnimationStartPosition(
  activeMenuItem: string,
  previousActiveMenuItem: string,
  menuItemsRef: React.MutableRefObject<HTMLElement[]>
) {
  return useMemo(() => {
    const activeElement = menuItemsRef.current.find((el) => {
      if (!el) return null
      const itemName = el.dataset.pathname
      return itemName === activeMenuItem
    })
    const previousElement = menuItemsRef.current.find((el) => {
      if (!el) return null
      return el.dataset.pathname === previousActiveMenuItem
    })
    const previousIndex = menuItemsRef.current.findIndex((el) => {
      if (!el) return -1
      return el.dataset.pathname === previousActiveMenuItem
    })
    const currentIndex = menuItemsRef.current.findIndex((el) => {
      if (!el) return -1
      return el.dataset.pathname === activeMenuItem
    })
    if (!previousElement || !activeElement) return '0'
    if (previousActiveMenuItem === activeMenuItem) {
      return `${activeElement.offsetLeft}px`
    }
    const currentElementRect = activeElement.getBoundingClientRect()
    const previousElementRect = previousElement.getBoundingClientRect()
    const slideFrom = previousIndex < currentIndex ? 'left' : 'right'
    if (slideFrom === 'left') {
      return `-${
        activeElement.offsetLeft -
        previousElement.offsetLeft -
        previousElement.offsetWidth
      }px`
    } else {
      return `${previousElementRect.left - currentElementRect.left}px`
    }
  }, [activeMenuItem, previousActiveMenuItem, menuItemsRef])
}

const useActiveMenuItem = () => {
  const { ownProfile } = React.useContext(ProfilePageContext)
  const { pathname } = useRouter()

  const isProfileSectionActive = useMemo(() => {
    return (
      ownProfile &&
      (pathname.includes('projects') ||
        pathname.includes('network') ||
        pathname === `/[username]`)
    )
  }, [ownProfile, pathname])

  const isNetworkSectionActive = useMemo(() => {
    return (
      ownProfile &&
      (pathname.includes('connections') ||
        pathname.includes('invitations') ||
        pathname.includes('network'))
    )
  }, [ownProfile, pathname])

  return useMemo(() => {
    if (pathname.includes('inbox')) return 'inbox'
    if (pathname.includes('discover')) return 'discover'
    if (!ownProfile) return ''
    if (isNetworkSectionActive) return 'network'
    if (isProfileSectionActive && pathname.includes('projects'))
      return 'projects'
    return 'profile'
  }, [isProfileSectionActive, pathname, isNetworkSectionActive, ownProfile])
}
